import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ZapOffIcon, EyeOffIcon, FrownIcon, LightbulbOffIcon, TrendingDownIcon, ConstructionIcon, ScaleIcon, DollarSignIcon } from 'lucide-react';
export function ProblemSection() {
  const problems = [{
    icon: <FrownIcon className="w-8 h-8 text-red-400" data-id="element-264" />,
    title: 'Frustración inicial',
    desc: 'Estar en el taller rodeado de herramientas, con cables desordenados y luces desiguales, sintiendo que nada encaja.'
  }, {
    icon: <LightbulbOffIcon className="w-8 h-8 text-orange-400" data-id="element-265" />,
    title: 'Error con las luces',
    desc: 'Esa concentración que se convierte en confusión al ver que las tiras LED no quedan alineadas y el reflejo se ve distorsionado.'
  }, {
    icon: <TrendingDownIcon className="w-8 h-8 text-gray-400" data-id="element-266" />,
    title: 'Sin ventas ni resultados',
    desc: 'Ver tus mesas terminadas en el garaje mientras revisás el celular esperando una venta que no llega. Desmotivación total.'
  }, {
    icon: <ZapOffIcon className="w-8 h-8 text-red-500" data-id="element-267" />,
    title: 'No funciona',
    desc: 'El momento de estrés al intentar encender el proyecto y que no pase nada. Cables expuestos y sin saber por dónde empezar.'
  }, {
    icon: <ConstructionIcon className="w-8 h-8 text-yellow-500" data-id="element-268" />,
    title: 'Caos total',
    desc: 'Sentirse abrumado entre planos, materiales y LEDs desparramados por todos lados, sin un camino claro al éxito.'
  }, {
    icon: <ScaleIcon className="w-8 h-8 text-blue-400" data-id="element-269" />,
    title: 'Impacto visual pobre',
    desc: 'La frustración de comparar tu trabajo con uno profesional y notar que el tuyo se ve opaco, sin ese efecto infinito real.'
  }, {
    icon: <DollarSignIcon className="w-8 h-8 text-green-500" data-id="element-270" />,
    title: 'Dolor económico',
    desc: 'Mirar los recibos de materiales caros y ver un producto sin terminar, preocupado por la inversión perdida.'
  }];
  return <section className="py-16 bg-[#121212] text-white relative z-10 overflow-hidden" data-id="element-271">
      {/* Chaotic storm textures - blurred distorted shapes */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-30 overflow-hidden" data-id="element-272">
        <div className="absolute top-10 left-10 w-96 h-96 bg-red-900/20 rounded-full blur-[120px]" data-id="element-273" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gray-800/30 rounded-full blur-[100px]" data-id="element-274" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-red-800/15 blur-[80px] skew-x-12" data-id="element-275" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20" data-id="element-276" />
      </div>

      <div className="max-w-5xl mx-auto px-4 relative z-10" data-id="element-277">
        <div className="text-center mb-8" data-id="element-278">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl lg:text-6xl font-black mb-3 tracking-tight text-[#dc2626] leading-[1.05] uppercase" style={{
          textShadow: '0 2px 10px rgba(220, 38, 38, 0.3)'
        }} data-id="element-279">
            EL PROBLEMA REAL
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-280">
            Si te identificás con alguno de estos problemas, este ebook es para
            vos. Dejá de frustrarte y empezá a crear con seguridad.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-281">
          {problems.map((problem, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="relative group" data-id="element-282">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent rounded-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-283" />
              <div className="p-6 md:p-8 border border-[#27272a] rounded-2xl bg-[#0a0514]/50 backdrop-blur-sm hover:border-red-500/30 transition-colors duration-300 h-full" data-id="element-284">
                <div className="bg-[#0a0514] w-14 h-14 rounded-xl flex items-center justify-center shadow-inner mb-6 border border-red-900/30 group-hover:scale-110 transition-transform duration-300" data-id="element-285">
                  <span className="text-2xl" data-id="element-286">{problem.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 tracking-wide" data-id="element-287">
                  {problem.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm font-medium" data-id="element-288">
                  {problem.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}